import BlockContent from '@sanity/block-content-to-react';
import { graphql, useStaticQuery } from 'gatsby';
import groq from 'groq';
import React, { useRef } from 'react';

import { clsx } from '@digital-spiders/misc-utils';
import { useWindowDimensions } from '@digital-spiders/react-hooks';
import { ButtonLinkType } from '../../graphql-fragments/ButtonLink';
import { SanityImageType } from '../../graphql-fragments/SanityImage';
import PreviewLoadingScreen from '../../preview/PreviewLoadingScreen';
import { usePreviewData } from '../../preview/previewUtils';
import serializers from '../../serializers';
import { BREAKPOINTS } from '../../styles/breakpoint';
import { PageDocument, RawPortableText } from '../../types/types';
import { useCloser } from '../../utils/projectUtils';
import { replaceNewLinesWithBr } from '../../utils/utils';
import { ModuleBackgroundColor } from '../ModulesContent';
import ButtonLink from './ButtonLink';
import Calendar from './Calendar';
import * as styles from './Hero.module.scss';
import Image from './Image';
import ModuleLayout from './ModuleLayout';
import RatingStars from './RatingStars';
import Slider from './Slider';
import Video from './Video';

const ELEMENT_ANIMATION_DURATION = 1.5;

export type HeroProps = {
  moduleId?: string;
  pageColor: ModuleBackgroundColor;
  superTitle?: string;
  title: string;
  subtitle?: string;
  _rawText?: RawPortableText;
  button?: ButtonLinkType;
  showClientBrandsLogos?: boolean;
  reviews?: Array<string>;
  withColoredBackground?: boolean;
  contentLayout?: 'row' | 'column';
  withCloserSupport?: boolean;
  titleWithCloserParameter?: string;
  videoUrl?: string;
  image?: SanityImageType;
  withCalendar?: boolean;
  calendarType?: 'onceHub' | 'calendly';
  pageToGoAfterSubmit?: PageDocument;
  textAndImageAlignment?: 'imageBelowText' | 'imageBesideText';
  textAlignment?: 'left' | 'center';
  imageFitOption?: 'contain' | 'cover';
};

interface QueryData {
  allSanityClientBrand: {
    nodes: Array<{
      darkLogo: SanityImageType;
      whiteLogo: SanityImageType;
      title: string;
    }>;
  };
  sanityHpWebsiteSettings: {
    defaultScheduleOnceCalendarId: string;
    defaultCalendlyCalendarLink: string;
  };
}

const Hero = ({
  moduleId,
  superTitle,
  title,
  subtitle,
  _rawText,
  button,
  image,
  pageColor,
  showClientBrandsLogos,
  textAndImageAlignment,
  textAlignment,
  imageFitOption,
  reviews,
  withColoredBackground,
  withCloserSupport,
  titleWithCloserParameter,
  contentLayout,
  videoUrl,
  withCalendar,
  calendarType,
  pageToGoAfterSubmit,
}: HeroProps): React.ReactElement => {
  const staticData = useStaticQuery<QueryData>(graphql`
    {
      allSanityClientBrand(sort: { orderRank: ASC }) {
        nodes {
          title
          darkLogo {
            ...SanityImage
          }
          whiteLogo {
            ...SanityImage
          }
        }
      }
      sanityHpWebsiteSettings {
        defaultScheduleOnceCalendarId
        defaultCalendlyCalendarLink
      }
    }
  `);

  const groqQuery = groq`{
    "allSanityClientBrand": {
      "nodes": *[_type == "clientBrand"] | order(orderRank asc) {
        ...
      }
    }
  }`;

  const data = usePreviewData<QueryData>(staticData, {
    groqQuery,
  });

  if (!data) {
    return <PreviewLoadingScreen></PreviewLoadingScreen>;
  }

  const { nodes: clientBrands } = data.allSanityClientBrand;

  const closer = useCloser();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const windowWidth = useWindowDimensions()?.width;

  return (
    <ModuleLayout
      id={moduleId}
      className={clsx(
        styles.container,
        withColoredBackground && styles.withColoredBackground,
        withColoredBackground && contentLayout === 'column' && styles.coloredColumnLayout,
        withColoredBackground && contentLayout === 'row' && styles.coloredRowLayout,
        withCalendar && styles.withCalendar,
      )}
      contentClassName={clsx(
        styles.contentContainer,
        showClientBrandsLogos && styles.withMinHeight,
      )}
      childrenClassName={clsx(
        image && textAndImageAlignment === 'imageBelowText' && styles.imageBelowText,
        image &&
          textAndImageAlignment === 'imageBelowText' &&
          textAlignment === 'center' &&
          styles.centeredText,
        image && textAndImageAlignment === 'imageBesideText' && styles.imageBesideText,
      )}
      currentModuleBgColor={withColoredBackground ? 'dark' : 'light'}
    >
      <div ref={containerRef} className={styles.content}>
        <div className={styles.textContainer}>
          {closer && withCloserSupport && (
            <div className={styles.closerContainer}>
              <div className={styles.closerImageContainer}>
                <Image image={closer.image} />
              </div>
              <div className={styles.closerTextContainer}>
                <span className={styles.closerSpan}>Meeting with</span>
                <span className={styles.closerName}>{closer.name}</span>
              </div>
            </div>
          )}
          {superTitle && (
            <div className={styles.superTitleContainer}>
              <p className={styles.superTitle}>{superTitle}</p>
            </div>
          )}
          <h1 className={styles.title}>
            {replaceNewLinesWithBr(
              closer && withCloserSupport && titleWithCloserParameter
                ? titleWithCloserParameter.replace('{closerName}', closer.name)
                : title,
            )}
          </h1>
          {subtitle && <h2 className={styles.subtitle}>{subtitle}</h2>}
          {_rawText && (
            <BlockContent
              renderContainerOnSingleChild
              className={styles.text}
              blocks={_rawText}
              serializers={serializers}
            />
          )}
          {button && (
            <ButtonLink to={button} className={styles.button}>
              {button.title}
            </ButtonLink>
          )}

          {reviews && reviews.length > 0 && (
            <div className={styles.reviewsContainer}>
              <div className={styles.divider} />
              <div className={styles.reviewsWrapper}>
                {reviews.map((review, i) => (
                  <div className={styles.reviewContainer} key={i}>
                    <RatingStars rating={5} fillColor="#FFAC0C" starsSize={16} gapSize={5} />
                    <p className={styles.review}>"{review}"</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {withColoredBackground && (
          <div className={styles.mediaContainer}>
            {(videoUrl && <Video url={videoUrl} />) ||
              (withCalendar && (
                <Calendar
                  className={styles.calendar}
                  pageToGoAfterSubmit={pageToGoAfterSubmit}
                  calendarType={calendarType}
                  calendarId={data.sanityHpWebsiteSettings.defaultScheduleOnceCalendarId}
                  calendarLink={data.sanityHpWebsiteSettings.defaultCalendlyCalendarLink}
                />
              )) ||
              (image && <Image image={image} />)}
          </div>
        )}

        {showClientBrandsLogos && clientBrands && clientBrands.length > 0 && (
          <div className={styles.brandsContainer}>
            <div className={styles.divider}></div>
            <span className={styles.brandsContainerTitle}>
              Trusted By The World's Most Loved Brands
            </span>
            <div className={styles.sliderWrapper}>
              <Slider
                animationDuration={ELEMENT_ANIMATION_DURATION * clientBrands.length + 1}
                noAnimation={!windowWidth || windowWidth >= BREAKPOINTS.tabletHorizontal}
                className={styles.slider}
              >
                <div className={styles.logosContainer}>
                  {clientBrands.map((brand, i) => (
                    <div className={styles.logoContainer} key={i}>
                      <Image
                        alt={brand.title}
                        title={brand.title}
                        image={pageColor === 'dark' ? brand.whiteLogo : brand.darkLogo}
                      />
                    </div>
                  ))}
                </div>
              </Slider>
            </div>
          </div>
        )}
      </div>
      {image && (
        <div
          className={clsx(
            styles.imageContainer,
            imageFitOption === 'contain' && styles.containedImage,
          )}
        >
          <Image
            image={image}
            cover={imageFitOption === 'cover'}
            width={1920}
            height={textAndImageAlignment === 'imageBesideText' ? 1080 : 685}
          />
        </div>
      )}
      {withColoredBackground && contentLayout === 'column' && (
        <div className={styles.whiteSpace}></div>
      )}
    </ModuleLayout>
  );
};

export default Hero;
